import React, { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReplayIcon from "@mui/icons-material/Replay";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  AppBar,
  Body,
  ErrorCard,
  LoadingCard,
  LoadingPage,
  SuccessCard,
} from "../components";
import {
  useDownloadReceiptMutation,
  useQueryPaymentStatus,
  readPriceSearch,
  useQueryPrice,
  useQueryServiceByServiceId,
} from "../api/queries";
import { getFailureDisplay, getStorageToken, isErrorResponse } from "../utils";
import { useTheme } from "../hooks";
import { Alert, AlertTitle, Box, Button } from "@mui/material";
import { TRANSACTION_STATUS } from "../constants";
import { DEFAULT_PHONE_COUNTRY } from "../constants/phoneCountries";
import { displayPhone } from "../utils/displayMask";
import { useTranslation } from "react-i18next";
import { getContrastTextColor } from "../utils/colorManager";
import { useGlobalContext } from "../context/globalContext";
import { usePortal } from "../context/portalContext";
import { useOptionalPayment } from "../context/paymentContext";
import { useOptionalSchoolFee } from "../context/schoolFeeContext";
import { CorporateService } from "../models/Service";
import ContactSupport from "../components/ContactSupport";
import appConfig from "../config/config";
import { pageColumn } from "../components/layout/container/pageColumn";
import { getSupportChannelConfig } from "../utils/getSupportChannelConfig";

const Transaction = () => {
  const { t, i18n } = useTranslation();
  const token = getStorageToken();
  const { id = "", transactionId = "" } = useParams();
  const [error, setError] = useState<string>("");
  const [reference, setReference] = useState<string>("");
  const navigate = useNavigate();

  const location = useLocation();
  const phoneNumber: string | undefined = location.state?.phoneNumber;
  const methodName: string | undefined = location.state?.methodName;
  // Router state, so a reload drops it and the row goes rather than guess.
  const paidWith = [
    methodName,
    phoneNumber ? displayPhone(phoneNumber, DEFAULT_PHONE_COUNTRY) : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const { pending, success, errored } = TRANSACTION_STATUS;
  const [status, setStatus] = useState<string>(pending);
  const downloadMutation = useDownloadReceiptMutation({
    authorisation: token!,
    itemId: transactionId,
  });

  const { currency } = useGlobalContext();

  // The journey this payment came from, held in memory by the journey scope
  // above this route. Absent after a reload or on a bare link.
  const payment = useOptionalPayment();
  const schoolFee = useOptionalSchoolFee();
  const resetPayment = payment?.reset;
  const resetSchoolFee = schoolFee?.reset;
  const feeName = schoolFee?.selectedFee?.name ?? "";
  const customerName = payment?.customerUser?.name ?? "";
  // Taken at the moment of success, because the journey is then cleared.
  const [paidFor, setPaidFor] = useState<{
    fee: string;
    customer: string;
  } | null>(null);

  const { theme } = useTheme();
  // The school and the branding come from the entry this visit resolved, not
  // from a page name read out of the address: a payment link has none there.
  // It also ends two lookups this page repeated on every mount.
  const { scope, state, homePath } = usePortal();

  const corporateId = scope?.entity_id ? String(scope.entity_id) : "";

  const service = useQueryServiceByServiceId({
    corporateId,
    serviceId: id,
    enabled: !!token && !!id && !!corporateId,
    authorisation: token || "",
  });

  const {
    data: paymentStatus,
    isLoading: isLoadingPayment,
    error: paymentError,
  } = useQueryPaymentStatus({
    paymentItemId: transactionId,
    enabled: !!token && !!transactionId,
    authorisation: token || "",
  });


  const [searchParams] = useSearchParams();
  const identifiers = readPriceSearch(searchParams);
  const recordedAmount = paymentStatus?.payment?.amount;
  const pricePayload =
    identifiers &&
      recordedAmount !== undefined &&
      Number.isFinite(Number(recordedAmount))
      ? { ...identifiers, amount: recordedAmount }
      : undefined;
  const { data: priceData, isInitialLoading: isLoadingPrice } = useQueryPrice({
    payload: pricePayload,
    enabled: !!token,
    authorisation: token || "",
  });
  const price = priceData && !isErrorResponse(priceData) ? priceData : undefined;
  const totalAmount = String(price?.total_amount ?? "");
  const totalFees = String(price?.total_fees ?? "");
  const instrumentFee = String(price?.instrument_fee ?? "");
  const registrationFee = String(price?.registration_fee ?? "");

  useEffect(() => {
    // A failed status read is not a failed payment. The payment has already
    // been accepted at this point, so keep the honest pending view visible
    // while the query retries/polls for an explicit final business status.
    if (paymentError) {
      setStatus(pending);
      setError("");
      setReference("");
      return;
    }

    if (!paymentStatus) return;

    const payment = paymentStatus.payment;
    const rawStatus = (payment?.status || payment?.data?.status || "")
      .toString()
      .toUpperCase();

    if (rawStatus === "SUCCESS" || rawStatus === "PAID") {
      setStatus(success);
      setError("");
      setReference(paymentStatus.reference);
    } else if (
      rawStatus === "FAILED" ||
      rawStatus === "ERROR" ||
      rawStatus === "DECLINED"
    ) {
      // `error` is the field that carries the provider's reason for the
      // failure. It arrives on a 200, so nothing on the seam's error path
      // ever sees it and the reference has to come from the envelope.
      const failure = getFailureDisplay(
        {
          body: payment?.error ?? payment?.data?.error,
          correlationId: paymentStatus.reference,
        },
        t,
        i18n.language
      );
      setStatus(errored);
      setError(failure.message);
      setReference(failure.reference || paymentStatus.reference);
    } else {
      setStatus(pending);
      setError("");
      setReference("");
    }
  }, [paymentStatus, paymentError, t, i18n.language, success, errored, pending]);

  useEffect(() => {
    if (status !== success || paidFor) return;
    setPaidFor({ fee: feeName, customer: customerName });
    // A paid journey must not be offered again: browser back would land on
    // Review with every entry kept, one press from paying twice.
    resetPayment?.();
    resetSchoolFee?.();
  }, [
    status,
    success,
    paidFor,
    feeName,
    customerName,
    resetPayment,
    resetSchoolFee,
  ]);

  // Back to the journey this route sits under. Its scope kept every entry, so
  // the payer lands on Review or Confirm and nothing is sent until they
  // confirm again; after a reload the journey restarts at its first step.
  // Try again is the payer's deliberate choice to pay again, so it lifts the
  // mark that keeps a sent payment's Confirm out of reach.
  const tryAgain = () => {
    payment?.setSubmittedOutcome(null);
    navigate(location.pathname.replace(/\/transaction\/[^/]+\/?$/, ""), {
      replace: true,
    });
  };

  // Derived at render -- React Query owns it. Kept apart from `reference`,
  // the browser-minted correlation id that names a failure, not the payment.
  const paymentReference = paymentError
    ? ""
    : paymentStatus?.payment?.payment_reference ?? "";

  // Only a lookup still in flight holds the page back. An entry that cannot
  // be resolved does not: what the payer paid outranks how the page is
  // branded, so the result is shown on the neutral theme rather than hidden.
  // `isInitialLoading`, because with no school the service read is never
  // sent, and a read that is never sent reports itself as loading for good.
  if (
    state === "resolving" ||
    service?.isInitialLoading ||
    isLoadingPayment ||
    isLoadingPrice
  )
    return <LoadingPage />;

  const primary = {
    bgcolor: theme.color_primary,
    color: getContrastTextColor(theme.color_primary),
  };
  // An outline needs a stroke colour, not a surface colour: the secondary
  // fills panels behind text and can be near-white.
  const secondary = {
    borderColor: theme.color_primary,
    color: theme.color_primary,
  };

  const actions = (
    <>
      {status === errored && (
        <Button
          variant="contained"
          fullWidth
          size="large"
          sx={primary}
          startIcon={<ReplayIcon />}
          onClick={tryAgain}
        >
          {t("outcome.try_again")}
        </Button>
      )}
      {/* A failed payment produced no receipt to download. */}
      {status === success && (
        <Button
          variant="contained"
          fullWidth
          size="large"
          sx={primary}
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={() => {
            downloadMutation.mutate();
          }}
          disabled={!paymentReference}
        >
          {t("button.download_reciept")}
        </Button>
      )}
      <Button
        variant="outlined"
        fullWidth
        size="large"
        sx={secondary}
        startIcon={<ArrowBackIcon />}
        onClick={() => {
          navigate(homePath);
        }}
      >
        {t("stepper_4.make_other_payment")}
      </Button>
      {/* One screen, one place to report: the support links sit under the
          buttons, inside the same card. */}
      <ContactSupport
        signals={{ stage: status === errored ? "pay" : "receipt" }}
        facts={{
          reference,
          paymentReference,
          amount: totalAmount,
          route: "/transaction",
        }}
        config={getSupportChannelConfig(appConfig)}
        tone="brand"
        showPrompt={false}
      />
    </>
  );

  return (
    <Body>
      <AppBar theme={theme} />
      <Box sx={pageColumn}>
        {/* On a failure the outcome card carries the reason; one place only. */}
        {error && status !== errored && (
          <Alert
            severity={"error"}
            variant={"standard"}
            onClose={() => setError("")}
          >
            <AlertTitle sx={{ fontWeight: "bold" }}>
              {t("errors.title")}
            </AlertTitle>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
        {status === pending && (
          <LoadingCard
            totalAmount={totalAmount}
            currency={currency}
            phoneNumber={phoneNumber}
          />
        )}
        {status === success && (
          <SuccessCard
            totalAmount={totalAmount}
            currency={currency}
            totalFees={totalFees}
            instrumentFee={instrumentFee}
            registrationFee={registrationFee}
            paymentReference={paymentReference}
            paidWith={paidWith}
            paidFor={
              paidFor?.fee ||
              (service?.data as CorporateService | undefined)?.service_name ||
              ""
            }
            customer={paidFor?.customer}
            actions={actions}
          />
        )}
        {status === errored && (
          <ErrorCard
            totalAmount={totalAmount}
            currency={currency}
            errorMessage={error}
            reference={reference}
            paymentReference={paymentReference}
            actions={actions}
          />
        )}
        </Box>
      </Box>
    </Body>
  );
};

export default Transaction;
